// Quản lý tài liệu: dải KPI gọn (chi tiết ở trang Tổng quan), cây danh mục, tab trạng thái, bộ lọc đồng bộ URL, bảng có sắp xếp/phân trang, thao tác theo quyền
import { useMemo, useState } from 'react';
import { Button, DatePicker, Dropdown, Input, Select, Table, Tabs, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, CalendarClock, Download, Eye, EyeOff, FileCheck2, Files, Folder, FolderOpen, Hourglass, LayoutDashboard, Minus, MoreHorizontal,
  Pencil, Plus, RotateCcw, Search, XCircle,
} from 'lucide-react';
import DocShell from 'pages/Documents/components/DocShell';
import { QuickInfoDrawer, LabelTags } from 'pages/Documents/components/QuickInfo';
import useDocActions from 'pages/Documents/components/useDocActions';
import useDocsStore from 'pages/Documents/store';
import { BLOCKS, DEPARTMENTS, deptName, type Doc, type DocState } from 'pages/Documents/data';
import { buildCategoryTree, canViewDoc, categoryPath, descendantIds, docState, matches, recipientIds, type CategoryNode } from 'pages/Documents/logic';
import { FLAG_LABEL, docFlags, type Flag } from 'pages/Documents/stats';
import { EXT_GROUPS, STATE_META, StatePill, docLink, exportDocsExcel, fmtDate, fmtEffective, useDocAuth, usePeople } from 'pages/Documents/utils';

const TABS: { key: string; label: string; states?: DocState[] }[] = [
  { key: 'all', label: 'Tất cả' },
  { key: 'draft', label: 'Dự thảo', states: ['draft'] },
  { key: 'pending', label: 'Chờ duyệt', states: ['pending'] },
  { key: 'active', label: 'Còn hiệu lực', states: ['active'] },
  { key: 'upcoming', label: 'Chưa hiệu lực', states: ['upcoming'] },
  { key: 'expired', label: 'Hết hiệu lực', states: ['expired', 'replaced'] },
  { key: 'revoked', label: 'Thu hồi', states: ['revoked'] },
  { key: 'rejected', label: 'Bị từ chối', states: ['rejected'] },
];

export default function DocumentManagement() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can, me, viewer } = useDocAuth();
  const actions = useDocActions();
  const people = usePeople();
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);
  const allDocs = useDocsStore(s => s.docs);

  const [treeQ, setTreeQ] = useState('');
  const [openNodes, setOpenNodes] = useState<Set<string>>(() => new Set(categories.filter(c => !c.parentId).map(c => c.id)));
  const [info, setInfo] = useState<Doc | null>(null);

  const tab = params.get('tab') || 'all';
  const cat = params.get('cat') || '';
  const q = params.get('q') || '';
  const unit = params.get('unit') || '';
  const type = params.get('type') || '';
  const block = params.get('block') || '';
  const seen = params.get('seen') || '';
  const ext = params.get('ext') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';
  const flag = (params.get('flag') || '') as Flag | '';
  const setParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const resetFilters = () => setParams(new URLSearchParams(tab !== 'all' ? { tab } : {}), { replace: true });

  const docs = useMemo(() => allDocs.filter(d => !d.deletedAt && canViewDoc(d, viewer, people)), [allDocs, viewer, people]);
  const flagsById = useMemo(() => new Map(docs.map(d => [d.id, docFlags(d, people)])), [docs, people]);

  // Lọc theo mọi điều kiện trừ tab → dùng để đếm số trên tab
  const catIds = useMemo(() => (cat ? new Set([cat, ...descendantIds(categories, cat)]) : null), [cat, categories]);
  const extGroup = EXT_GROUPS.find(g => g.key === ext);
  const baseFiltered = useMemo(() => docs.filter(d =>
    matches(q, d.title, d.code, d.number) &&
    (!catIds || catIds.has(d.categoryId)) &&
    (!unit || d.unitId === unit) &&
    (!type || d.typeId === type) &&
    (!block || d.blockId === block) &&
    (!seen || (seen === 'seen' ? !!d.views[me.id] : !d.views[me.id])) &&
    (!extGroup || d.files.some(f => extGroup.exts.includes(f.ext))) &&
    (!from || dayjs(d.createdAt).format('YYYY-MM-DD') >= from) &&
    (!to || dayjs(d.createdAt).format('YYYY-MM-DD') <= to) &&
    (!flag || !!flagsById.get(d.id)?.includes(flag))),
  [docs, q, catIds, unit, type, block, seen, extGroup, from, to, flag, flagsById, me.id]);
  const tabCounts = useMemo(() => {
    const m: Record<string, number> = { all: baseFiltered.length };
    TABS.forEach(t => { if (t.states) m[t.key] = baseFiltered.filter(d => t.states!.includes(docState(d))).length; });
    return m;
  }, [baseFiltered]);
  const current = TABS.find(t => t.key === tab) || TABS[0];
  const rows = current.states ? baseFiltered.filter(d => current.states!.includes(docState(d))) : baseFiltered;

  // ---- Dải KPI gọn (toàn bộ tài liệu được xem, không phụ thuộc bộ lọc) — chi tiết ở trang Tổng quan
  const stats = useMemo(() => {
    const flagCount = (f: Flag) => docs.filter(d => flagsById.get(d.id)?.includes(f)).length;
    return {
      active: docs.filter(d => docState(d) === 'active').length,
      pending: docs.filter(d => d.status === 'pending').length,
      overdue: flagCount('overdue'),
      expiring: flagCount('expiring'),
      unseen: docs.filter(d => d.status === 'published' && !d.views[me.id] && d.createdBy !== me.id).length,
      attention: flagCount('overdue') + flagCount('expiring') + flagCount('lowread') + flagCount('comments'),
    };
  }, [docs, flagsById, me.id]);

  // ---- Cây danh mục với số lượng
  const countByCat = useMemo(() => {
    const direct: Record<string, number> = {};
    docs.forEach(d => { direct[d.categoryId] = (direct[d.categoryId] || 0) + 1; });
    const out: Record<string, number> = {};
    categories.forEach(c => { out[c.id] = [c.id, ...descendantIds(categories, c.id)].reduce((s, id) => s + (direct[id] || 0), 0); });
    return out;
  }, [docs, categories]);
  const tree = useMemo(() => buildCategoryTree(categories.filter(c => c.visible), c => matches(treeQ, c.name, c.code)), [categories, treeQ]);
  const toggleNode = (id: string) => setOpenNodes(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const renderNode = (n: CategoryNode, depth: number): React.ReactNode => {
    const open = !!treeQ || openNodes.has(n.id);
    const active = cat === n.id;
    return (
      <li key={n.id}>
        <div
          className={`group flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 transition-colors ${active ? 'bg-[#ecf6f9] text-[#2c6b7f]' : 'hover:bg-slate-50'}`}
          style={{ paddingLeft: 8 + depth * 16 }}
          onClick={() => setParam({ cat: active ? '' : n.id })}
        >
          {n.children ? (
            <button onClick={e => { e.stopPropagation(); toggleNode(n.id); }} className="flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border border-slate-300 bg-white text-slate-500">
              {open ? <Minus size={10} strokeWidth={3} /> : <Plus size={10} strokeWidth={3} />}
            </button>
          ) : <span className="w-4 shrink-0" />}
          {active ? <FolderOpen size={17} className="shrink-0 text-[#3e91ab]" /> : <Folder size={17} className="shrink-0 text-[#3e91ab]/70" />}
          <span className={`min-w-0 flex-1 truncate text-[13px] ${active ? 'font-semibold' : 'text-slate-700'}`}>{n.name}</span>
          <span className="text-[11px] text-slate-400">{countByCat[n.id] || 0}</span>
        </div>
        {open && n.children && <ul>{n.children.map(c => renderNode(c, depth + 1))}</ul>}
      </li>
    );
  };

  const exportExcel = () => {
    if (!rows.length) return message.warning('Không có dữ liệu để xuất');
    exportDocsExcel(rows, types, categories, people, `tai-lieu_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
  };

  const filterCount = [q, cat, unit, type, block, seen, ext, from, flag].filter(Boolean).length;

  const columns: ColumnsType<Doc> = [
    {
      title: 'Mã tài liệu', dataIndex: 'code', width: 190, fixed: 'left', sorter: (a, b) => a.code.localeCompare(b.code),
      render: (v, d) => <Link to={docLink(d.id)} className="text-[13px] font-medium text-[#357f96] hover:underline">{v}</Link>,
    },
    {
      title: 'Tên tài liệu', dataIndex: 'title', width: 320, sorter: (a, b) => a.title.localeCompare(b.title),
      render: (v, d) => {
        const unseen = d.status === 'published' && !d.views[me.id] && d.createdBy !== me.id;
        return (
          <div className="flex items-start gap-2">
            {unseen && <Tooltip title="Bạn chưa xem"><span className="mt-1.5 h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-500" /></Tooltip>}
            <div className="min-w-0">
              <Link to={docLink(d.id)} className={`hover:text-[#357f96] hover:underline ${unseen ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>{v}</Link>
              <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-slate-400">
                <span>{categoryPath(categories, d.categoryId)}</span>
                {d.replacesId && <span className="rounded bg-violet-50 px-1 text-violet-600">Ban hành lại</span>}
                <LabelTags docId={d.id} />
              </div>
            </div>
          </div>
        );
      },
    },
    { title: 'Loại', dataIndex: 'typeId', width: 120, render: v => types.find(t => t.id === v)?.name },
    { title: 'Hiệu lực', key: 'eff', width: 170, sorter: (a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom), render: (_v, d) => <span className="text-[13px] text-slate-600">{fmtEffective(d)}</span> },
    { title: 'Đơn vị ban hành', dataIndex: 'unitId', width: 150, render: v => deptName(v) },
    { title: 'Trạng thái', key: 'state', width: 140, render: (_v, d) => <StatePill doc={d} /> },
    {
      title: <Tooltip title="Số người đã xem / số người được chia sẻ">Đã xem</Tooltip>, key: 'views', width: 90, align: 'center',
      render: (_v, d) => {
        const total = recipientIds(d, people).length;
        const n = recipientIds(d, people).filter(id => d.views[id]).length;
        return d.status === 'published' || d.status === 'revoked' ? <span className="text-[13px] text-slate-600">{n}/{total}</span> : <span className="text-slate-300">—</span>;
      },
    },
    { title: 'Ngày tạo', dataIndex: 'createdAt', width: 110, defaultSortOrder: 'descend', sorter: (a, b) => a.createdAt.localeCompare(b.createdAt), render: v => <span className="text-[13px] text-slate-500">{fmtDate(v)}</span> },
    {
      title: 'Hành động', key: 'act', width: 120, align: 'center', fixed: 'right',
      render: (_v, d) => {
        const more = [
          actions.perm.approve(d) && { key: 'approve', label: 'Duyệt & ban hành', onClick: () => actions.approve(d) },
          actions.perm.approve(d) && { key: 'reject', label: 'Từ chối', danger: true, onClick: () => actions.reject(d) },
          actions.perm.submit(d) && { key: 'submit', label: 'Gửi duyệt', onClick: () => actions.submit(d) },
          actions.perm.withdraw(d) && { key: 'withdraw', label: 'Rút lại về dự thảo', onClick: () => actions.withdraw(d) },
          actions.perm.reissue(d) && { key: 'reissue', label: 'Ban hành lại', onClick: () => navigate(`/add-new-document?reissue=${d.id}`) },
          actions.perm.revoke(d) && { key: 'revoke', label: 'Thu hồi', danger: true, onClick: () => actions.revoke(d) },
          actions.perm.delete(d) && { key: 'delete', label: 'Xóa', danger: true, onClick: () => actions.remove(d) },
        ].filter(Boolean) as { key: string; label: string; danger?: boolean; onClick: () => void }[];
        return (
          <div className="flex items-center justify-center gap-2.5">
            <Tooltip title="Xem nhanh"><button onClick={() => setInfo(d)} className="text-slate-400 hover:text-[#357f96]"><Eye size={16} /></button></Tooltip>
            {actions.perm.edit(d) && <Tooltip title={d.status === 'published' ? 'Sửa phân quyền' : 'Sửa'}><button onClick={() => navigate(`/add-new-document?id=${d.id}`)} className="text-slate-400 hover:text-[#357f96]"><Pencil size={16} /></button></Tooltip>}
            {more.length > 0 && (
              <Dropdown trigger={['click']} menu={{ items: more.map(m => ({ key: m.key, label: m.label, danger: m.danger, onClick: m.onClick })) }}>
                <button className="text-slate-400 hover:text-[#357f96]"><MoreHorizontal size={17} /></button>
              </Dropdown>
            )}
          </div>
        );
      },
    },
  ];

  const strip: { key: string; label: string; value: number; icon: React.ReactNode; tone: string; active: boolean; onClick: () => void }[] = [
    { key: 'active', label: 'Còn hiệu lực', value: stats.active, icon: <FileCheck2 size={16} />, tone: 'text-emerald-600 bg-emerald-50', active: tab === 'active' && !flag, onClick: () => setParam({ tab: 'active', flag: '' }) },
    { key: 'pending', label: 'Chờ duyệt', value: stats.pending, icon: <Hourglass size={16} />, tone: 'text-amber-600 bg-amber-50', active: tab === 'pending' && !flag, onClick: () => setParam({ tab: 'pending', flag: '' }) },
    { key: 'overdue', label: 'Quá hạn duyệt', value: stats.overdue, icon: <AlertTriangle size={16} />, tone: 'text-red-600 bg-red-50', active: flag === 'overdue', onClick: () => setParam({ tab: '', flag: flag === 'overdue' ? '' : 'overdue' }) },
    { key: 'expiring', label: 'Sắp hết hiệu lực', value: stats.expiring, icon: <CalendarClock size={16} />, tone: 'text-orange-600 bg-orange-50', active: flag === 'expiring', onClick: () => setParam({ tab: '', flag: flag === 'expiring' ? '' : 'expiring' }) },
    { key: 'unseen', label: 'Tôi chưa xem', value: stats.unseen, icon: <EyeOff size={16} />, tone: 'text-violet-600 bg-violet-50', active: seen === 'unseen', onClick: () => setParam({ seen: seen === 'unseen' ? '' : 'unseen' }) },
  ];

  return (
    <DocShell
      title="Quản lý tài liệu"
      subtitle={`${docs.length} tài liệu · ${stats.pending} chờ duyệt`}
      actions={<>
        <Button icon={<LayoutDashboard size={15} />} onClick={() => navigate('/document-dashboard')}>
          Tổng quan{stats.attention > 0 && <span className="ml-1 rounded-full bg-red-50 px-1.5 text-xs text-red-600">{stats.attention} cần xử lý</span>}
        </Button>
        {can('export') && <Button icon={<Download size={15} />} onClick={exportExcel}>Xuất Excel</Button>}
        {can('create') && <Button type="primary" icon={<Plus size={16} />} onClick={() => navigate('/add-new-document')}>Tạo mới</Button>}
      </>}
    >
      {actions.modals}
      <div className="mb-4 flex flex-wrap items-stretch gap-2">
        {strip.map(k => (
          <button
            key={k.key} onClick={k.onClick}
            className={`flex min-w-[150px] flex-1 items-center gap-3 rounded-lg border bg-white px-3 py-2 text-left transition-colors ${k.active ? 'border-[#3e91ab] ring-1 ring-[#3e91ab]/30' : 'border-slate-200 hover:border-[#3e91ab]/50'}`}
          >
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${k.tone}`}>{k.icon}</span>
            <span className="min-w-0">
              <span className="block text-[12px] text-slate-500">{k.label}</span>
              <span className="block text-lg font-bold leading-tight text-slate-800">{k.value}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        <aside className="flex max-h-[360px] w-full shrink-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white lg:max-h-[calc(100vh-180px)] lg:w-72">
          <div className="border-b border-slate-100 bg-slate-50 p-3">
            <Input size="small" allowClear prefix={<Search size={14} className="text-slate-400" />} placeholder="Tìm nhanh danh mục…" value={treeQ} onChange={e => setTreeQ(e.target.value)} />
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <div
              onClick={() => setParam({ cat: '' })}
              className={`mb-1 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] ${!cat ? 'bg-[#ecf6f9] font-semibold text-[#2c6b7f]' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <Files size={16} className="text-[#3e91ab]" /> <span className="flex-1">Tất cả danh mục</span><span className="text-[11px] text-slate-400">{docs.length}</span>
            </div>
            {tree.length ? <ul>{tree.map(n => renderNode(n, 0))}</ul> : <div className="p-4 text-center text-xs text-slate-400">Không có danh mục phù hợp</div>}
          </div>
        </aside>

        <section className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white">
          <Tabs
            activeKey={current.key} onChange={k => setParam({ tab: k === 'all' ? '' : k })} className="px-4 [&_.ant-tabs-nav]:!mb-0"
            items={TABS.map(t => ({
              key: t.key,
              label: <span>{t.label} <span className={`ml-1 rounded-full px-1.5 text-xs ${t.key === 'pending' && tabCounts[t.key] ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{tabCounts[t.key] ?? 0}</span></span>,
            }))}
          />
          <div className="flex flex-wrap items-center gap-2 border-y border-slate-100 bg-slate-50/60 p-3">
            <Input allowClear prefix={<Search size={15} className="text-slate-400" />} placeholder="Tên, mã, số tài liệu…" value={q} onChange={e => setParam({ q: e.target.value })} className="w-full sm:w-64" />
            <Select value={type} onChange={v => setParam({ type: v })} className="w-40" options={[{ value: '', label: 'Tất cả loại' }, ...types.map(t => ({ value: t.id, label: t.name }))]} />
            <Select value={unit} onChange={v => setParam({ unit: v })} className="w-44" options={[{ value: '', label: 'Tất cả đơn vị' }, ...DEPARTMENTS.map(d => ({ value: d.id, label: d.name }))]} />
            <Select value={block} onChange={v => setParam({ block: v })} className="w-52" options={[{ value: '', label: 'Tất cả khối' }, ...BLOCKS.map(b => ({ value: b.id, label: b.name }))]} />
            <Select value={seen} onChange={v => setParam({ seen: v })} className="w-36" options={[{ value: '', label: 'Tình trạng xem' }, { value: 'unseen', label: 'Chưa xem' }, { value: 'seen', label: 'Đã xem' }]} />
            <Tooltip title="Ngày tải lên">
              <DatePicker.RangePicker
                format="DD/MM/YYYY" placeholder={['Tải lên từ', 'đến']} className="w-60"
                value={[from ? dayjs(from) : null, to ? dayjs(to) : null]}
                onChange={v => setParam({ from: v?.[0]?.format('YYYY-MM-DD') || '', to: v?.[1]?.format('YYYY-MM-DD') || '' })}
              />
            </Tooltip>
            {flag && <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs text-amber-700">{FLAG_LABEL[flag]}<XCircle size={13} className="cursor-pointer" onClick={() => setParam({ flag: '' })} /></span>}
            {extGroup && <span className="flex items-center gap-1 rounded-full bg-[#ecf6f9] px-2.5 py-1 text-xs text-[#2c6b7f]">File {extGroup.label}<XCircle size={13} className="cursor-pointer" onClick={() => setParam({ ext: '' })} /></span>}
            {cat && <span className="flex max-w-[220px] items-center gap-1 truncate rounded-full bg-[#ecf6f9] px-2.5 py-1 text-xs text-[#2c6b7f]">{categoryPath(categories, cat)}<XCircle size={13} className="shrink-0 cursor-pointer" onClick={() => setParam({ cat: '' })} /></span>}
            <Button icon={<RotateCcw size={14} />} disabled={!filterCount} onClick={resetFilters}>Làm mới{filterCount ? ` (${filterCount})` : ''}</Button>
          </div>
          <Table<Doc>
            rowKey="id" columns={columns} dataSource={rows} size="middle" scroll={{ x: 1450 }}
            rowClassName={d => (d.status === 'published' && !d.views[me.id] && d.createdBy !== me.id ? 'bg-sky-50/40' : '')}
            locale={{ emptyText: filterCount || current.key !== 'all' ? 'Không tìm thấy tài liệu phù hợp' : 'Chưa có tài liệu' }}
            pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100], showTotal: (t, r) => `${r[0]}-${r[1]} trong ${t} tài liệu` }}
          />
        </section>
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">
        {Object.entries(STATE_META).map(([k, m]) => <span key={k} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.label}</span>)}
      </div>
      <QuickInfoDrawer doc={info} onClose={() => setInfo(null)} />
    </DocShell>
  );
}
